import Cookies from 'js-cookie';
import { pathToRegexp } from 'path-to-regexp';

import { PRIVATE_ROUTES } from '@/config/routes';

import { isRegExp } from './typeGaurds';

export const getValidSubdomain = (host?: string | null) => {
  let subdomain: string | null = null;

  if (!host && typeof window !== 'undefined') {
    // On client side, get the host from window
    host = window.location.host;
  }

  if (host?.includes('.')) {
    const candidate = host.split('.');
    const foundSubdomain = candidate.find(domain => domain.includes('backoffice'));
    if (foundSubdomain) {
      // Valid candidate
      subdomain = foundSubdomain;
    }
  }

  return subdomain;
};

export const getAppReservedWords = () => {
  return /^(product|products|checkout|cart|_next|enrollment|petprofile|thank-you|old-product|new-products|trypawtree|account|checkout-new|forgotpassword|orders|giftcards|account-settings-new|givingback|auto-order|AutoOrders|auto-order-list|ingredient-glossary|pawtrip-report|customer-referral|terms-of-use|meet-the-team|dsa|veterinary-advisory-council|pages|faq|product-videos|why-pawtree|pawtree-story|paw-club|pet-profile-creation-new|getStartedPetProfile|recommendation|Welcome|packs|confirmation|Login|Signup|logout|backoffice|reports|Product-Sales-Report|lead-generation-report|Customer-List|Customer-History|abandoned-cart-report|faststart-Report|Petpro-Report|MVR-Caps-Report|Prospect-List|Cancellation-Report|Qualification-History-Report|((title-reports)(.*)?)|((downline-orders)(.*)?)|tree-viewer|((volume-reports)(.*)?)|formerPetpro-Report|tools|giftcard-transactions-report|giftcards|managegiftcards|funded-giftcard-transactions-report|findapetpro|admin-giftcard-transactions-report|admin|managebreed|randomizer|commissions|login|logout|volumes|checkoutexperience|best-sellers|checkout-new|pending-ezship-orders|Monthly-Commission-Earnings|((profile)(.*)?)|myrecommendation(?:\/.*)?|customizedrecommendation(\/edit(\/\d+)?)?)$/;
};

export const getWebaliasAlongWithRestPath = (currentPath: string) => {
  const webAliasRegex =
    /^\/(.+?)\/(product|products|checkout|cart|thank-you|old-product|new-products|trypawtree|account|orders|giftcards|givingback|checkout-new|forgotpassword|auto-order|account-settings-new|AutoOrders|auto-order-list|ingredient-glossary|customer-referral|terms-of-use|pawtrip-report|meet-the-team|dsa|veterinary-advisory-council|pages|faq|product-videos|why-pawtree|pawtree-story|paw-club|petprofile|pet-profile-creation-new|getStartedPetProfile|recommendation|Welcome|enrollment|packs|confirmation|Login|Signup|reports|Product-Sales-Report|lead-generation-report|Customer-List|Customer-History|abandoned-cart-report|faststart-Report|Petpro-Report|MVR-Caps-Report|Prospect-List|Cancellation-Report|Qualification-History-Report|((title-reports)(.*)?)|((downline-orders)(.*)?)|tree-viewer|((volume-reports)(.*)?)|formerPetpro-Report|tools|login|logout|giftcard-transactions-report|giftcards|managegiftcards|funded-giftcard-transactions-report|admin-giftcard-transactions-report|findapetpro|admin|managebreed|randomizer|commissions|volumes|best-sellers|checkoutexperience|checkout-new|Monthly-Commission-Earnings|pending-ezship-orders|((profile)(.*)?)|myrecommendation(?=\/)|customizedrecommendation(?:\/edit(?:\/\d+)?)?)(.*)/;
  const [, webalias, ...restPath] = currentPath.match(webAliasRegex) || [];

  if (
    webalias?.includes('not-found') ||
    webalias?.includes('AutoOrders') ||
    webalias?.includes('auto-order') ||
    webalias?.includes('organization') ||
    webalias?.includes('giftcards') ||
    webalias?.includes('admin') ||
    webalias?.includes('login') ||
    webalias?.includes('logout')
  )
    return { webalias: null, urlPath: currentPath };

  const joinedPath = restPath.join('');

  if (!webalias) {
    const appReversedWordsRegex = getAppReservedWords();

    const pathChunks = (currentPath || '').replace('/', '').split('/');
    const newWebAlias = pathChunks.shift() ?? '';

    if (joinedPath === '' && !appReversedWordsRegex.test(newWebAlias)) return { webalias: newWebAlias, urlPath: `/` };

    return { webalias: '', urlPath: currentPath };
  }
  return { webalias, urlPath: `/${joinedPath}` };
};

export const generateApproperiateURL = (path: string, webalias?: string, excludeWebalias?: boolean) => {
  path = path.startsWith('/') ? path : `/${path}`;
  const backOfficeDefaultAlias = 'shopping';
  const subdomain = Cookies.get('subdomain') ?? '';
  const isBackOffice = subdomain.toLowerCase().includes('backoffice');

  if (excludeWebalias || typeof webalias === 'undefined') return path;

  if (!webalias) {
    if (isBackOffice) {
      return `/${backOfficeDefaultAlias}${path}`;
    }
    return `/pawtree${path}`;
  }
  return `/${isBackOffice ? webalias || backOfficeDefaultAlias : webalias}${path}`;
};

export const isEzShipRoute = (path: string) => {
  const productListingRegex = /\/(products)\/(.*)/;
  const productDetailRegex = /\/(product)\/(.*)/;

  const regexToGrabEzShipIdFromCategoryListingPage = /(\/products\/ezship\/(.*))/;
  const regexToGrabEzShipIdFromProductsListingPage = /(\/products\/[\d\w]+(.*)autoOrderID=([^&^\n\s]*))/;
  const regexToGrabEzShipIdFromProductDetailPage = /(\/product\/ezship\/(.*)\/(.*))/;

  return !!(
    path.match(regexToGrabEzShipIdFromCategoryListingPage) ||
    path.match(regexToGrabEzShipIdFromProductsListingPage) ||
    path.match(regexToGrabEzShipIdFromProductDetailPage) ||
    path.match(productListingRegex) ||
    path.match(productDetailRegex)
  );
};

export const getEzShipIdAlongWithRestPath = (currentPath: string) => {
  const regexToGrabEzShipIdFromCategoryListingPage = /(\/products\/ezship\/(.*))/;
  const regexToGrabEzShipIdFromProductsListingPage = /(\/products\/[\d\w]+(.*)autoOrderID=([^&^\n\s]*))/;
  const regexToGrabEzShipIdFromProductDetailPage = /(\/product\/ezship\/(.*)\/(.*))/;

  const matchCategoryListingPage = currentPath.match(regexToGrabEzShipIdFromCategoryListingPage);
  const matchProductListingPage = currentPath.match(regexToGrabEzShipIdFromProductsListingPage);
  const matchProductDetailPage = currentPath.match(regexToGrabEzShipIdFromProductDetailPage);

  // route --> /products/ezship/{ezshipId}
  if (matchCategoryListingPage) return { ezShipId: matchCategoryListingPage[2], urlPath: '/products' };

  // route --> /products/{productId}?autoOrderID={ezshipId}
  if (matchProductListingPage) return { ezShipId: matchProductListingPage[3], urlPath: currentPath };

  // route --> /product/ezship/{ezshipId}/{productId}
  if (matchProductDetailPage)
    return { ezShipId: matchProductDetailPage[2], urlPath: `/product/${matchProductDetailPage[3]}` };

  return { ezShipId: null, urlPath: currentPath };
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const generateEzShipURL = (path: string, ezshipId?: string) => {
  if (!ezshipId) return path;

  const searchPageRegex = /\/(products)\/(search)/;
  const categoryDetailRegexInEzShipFlow = /\/(products)\/(ezship)\/(.*)/;
  const productListingRegex = /\/(products)\/(.*)/;
  const productDetailRegex = /\/(product)\/(.*)/;
  const matchProductListingPage = path.match(productListingRegex);
  const matchProductDetailPage = path.match(productDetailRegex);
  const matchSearhPage = path.match(searchPageRegex);
  const matchCategoryDetailRegex = path.match(categoryDetailRegexInEzShipFlow);

  if (matchCategoryDetailRegex) {
    return '/products/ezship/' + ezshipId;
  }

  const existingAutoOrderRegex = /autoOrderID=\d+/gi;

  if (matchProductListingPage || matchSearhPage) {
    const newPath = path.replace(existingAutoOrderRegex, '');
    return `${newPath}${newPath.includes('?') ? (newPath.match(/^\?.+/) ? '&' : '') : '?'}autoOrderID=${ezshipId}`;
  }

  if (matchProductListingPage) {
    const newPath = path.replace(existingAutoOrderRegex, '');
    return `${newPath}${newPath.includes('?') ? (newPath.match(/^\?.+/) ? '&' : '') : '?'}autoOrderID=${ezshipId}`;
  }

  if (matchProductDetailPage) {
    const chunk = matchProductDetailPage[2].split('/').pop() || '';
    const parsedChunk =
      chunk.toLowerCase().startsWith('/ezship') || chunk.toLowerCase().startsWith('ezship')
        ? chunk.split('/').pop()
        : chunk;

    return `/product/ezship/${ezshipId}/${parsedChunk}`;
  }

  return path;
};

export const getRelativeUrlForInternalDomains = (link: string, isBackoffice?: boolean) => {
  if (isBackoffice) return link;

  return link
    .replace('https://pawtree.com', '')
    .replace('https://www.pawtree.com', '')
    .replace('https://shop.pawtree.com', '')
    .replace('https://v2-stg.pawtree.com', '')
    .replace('https://v2.pawtreedev.com', '')
    .replace('http://pawtree.com', '')
    .replace('http://www.pawtree.com', '')
    .replace('http://shop.pawtree.com', '')
    .replace('http://v2-stg.pawtree.com', '')
    .replace('http://v2.pawtreedev.com', '');
};

export function isPrivateRoute(urlToTest: string) {
  return PRIVATE_ROUTES.map(route => {
    try {
      return pathToRegexp(route);
    } catch (err) {
      return route;
    }
  }).some(rx => (isRegExp(rx) ? rx.test(urlToTest) : rx === urlToTest));
}

/**
 * Generates cart URL with appropriate query parameters based on session state and current context
 * @param isAuthenticated - Whether the user is authenticated
 * @param currentPathname - Optional current pathname (will use window.location.pathname if not provided)
 * @param currentSearchParams - Optional current search params (will use window.location.search if not provided)
 * @returns Cart URL string with appropriate query parameters
 */
export const getCartUrlWithRecommendationParam = (
  isAuthenticated: boolean,
  currentPathname?: string,
  currentSearchParams?: string
): string => {
  const baseCartUrl = '/cart';

  if (typeof window === 'undefined') {
    return baseCartUrl;
  }

  try {
    const pathname = currentPathname || window.location.pathname;
    const searchParams = currentSearchParams
      ? new URLSearchParams(currentSearchParams)
      : new URLSearchParams(window.location.search);
    const isGuestUser = searchParams.get('isGuestUser') === 'true';
    const customerId = searchParams.get('customerId');

    // Check sessionStorage flag for session-based tracking
    const hasVisitedRecommendationFlow = sessionStorage.getItem('hasVisitedRecommendationFlow') === 'true';
    // Also check current pathname for immediate case
    const isFromRecommendationFlow = pathname.includes('createrecommendationnew');

    // Build URL params
    const params = new URLSearchParams();

    // If user has visited recommendation flow in this session OR is currently on recommendation flow page
    if (hasVisitedRecommendationFlow || isFromRecommendationFlow) {
      params.set('isFromPetRecommendations', 'true');
      if (!isAuthenticated) {
        params.set('isGuestUser', 'true');
      }
    } else if (!isAuthenticated && isGuestUser) {
      // Existing logic: preserve isGuestUser param for guest users
      params.set('isGuestUser', 'true');
    }

    // Persist customerId if present (for guest/prospect users)
    if (customerId) {
      params.set('customerId', customerId);
    }

    const paramsString = params.toString();
    return paramsString ? `${baseCartUrl}?${paramsString}` : baseCartUrl;
  } catch (error) {
    console.warn('Error calculating cart URL:', error);
    return baseCartUrl;
  }
};
